<template>
  <div class="part-substitute--table manage-substitutes">
    <div style="margin: 15px 0 32px 0;">
      <PartInfoCard
        v-if="currentPart"
        :name="`${currentPart.mpn || ''} ${currentPart.brand || ''}`"
        :specification="currentPart.specification"
        :part-number="currentPart.part_identity ? currentPart.part_identity.part_number : ''">
      </PartInfoCard>
    </div>
    <div v-for="substitute_type in substituteTypes" :key="substitute_type" :class="`part-${substitute_type}`">
      <div>
        <h3 style="display: inline-block;">
          <Icon :type="icons[substitute_type]" /> {{ $t(`part.${substitute_type}`) }}
          <Tooltip
            :content="substitute_type === 'mutual_substitute' ? $t('part.substitution.L1') : $t('part.substitution.L2')"
            max-width="200">
            <Icon type="md-help-circle" />
          </Tooltip>
        </h3>
        <div style="display: flex; max-width: 500px; margin-left: auto; margin-right: 0; margin-bottom: 10px;">
          <ManufacturerPartPicker
            style="width: 100%;"
            v-model="selectedPartId[substitute_type]"
            :label="false">
          </ManufacturerPartPicker>&nbsp;
          <Button type="primary" @click="addPartToSubsituteCollection(substitute_type, selectedPartId)" :disabled="saving || showMergeConfirm">{{ $t('add') }}</Button>
        </div>
      </div>
      <Table :columns="columns" :data="items[substitute_type]"></Table>
      <div v-if="substitute_type === 'mutual_substitute'">
        <div v-show="showMergeConfirm" style="margin-top: 15px; padding: 5px; border: 1px solid red;">
          <h4 class="warning" style="margin-bottom: 5px;"><Icon type="ios-warning" />{{ $t('part.merge_mututal_substitute_collections_confirm') }}</h4>
          <Table :columns="columns" :data="mutualSubstituteToBeMerged['items']"></Table>
          <div class="text-align-right" style="margin-top: 10px;">
            <Button type="warning" @click="handleMerge(substitute_type)" :saving="mergeSaving">{{ $t('part.merge') }}</Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ManufacturerPartPicker from '@/view/part/components/manufacturer-part-picker'
import PartInfoCard from '@/view/part/components/widgets/part-info-card'
export default {
  name: 'PartSubstituteTable',
  components: {
    ManufacturerPartPicker,
    PartInfoCard
  },
  props: {
    items: Object,
    icons: Object,
    substituteCollectionIds: Object,
    currentPartId: Number,
    currentPart: Object
  },
  data () {
    return {
      saving: false,
      showMergeConfirm: false,
      mergeSaving: false,
      substituteTypes: ['mutual_substitute', 'unilateral_substitute'],
      selectedPartId: { mutual_substitute: null, unilateral_substitute: null },
      mutualSubstituteToBeMerged: {},
      columns: [
        {
          title: this.$t('part.info.name'),
          key: 'name',
          render: (h, params) => {
            return h('span', params.row.manufacturer_part.chinese_name)
          }
        },
        {
          title: this.$t('part.info.mpn'),
          key: 'mpn',
          minWidth: 90,
          render: (h, params) => {
            return h('span', params.row.manufacturer_part.mpn || '-')
          }
        },
        {
          title: this.$t('part.info.brand'),
          key: 'brand',
          minWidth: 60,
          render: (h, params) => {
            return h('span', params.row.manufacturer_part.brand || '-')
          }
        },
        {
          title: this.$t('part.info.specification'),
          minWidth: 200,
          key: 'specification',
          render: (h, params) => {
            return h('span', params.row.manufacturer_part.specification)
          }
        },
        {
          title: this.$t('part.info.part_number'),
          key: 'part_identity',
          minWidth: 110,
          render: (h, params) => {
            return h('span', params.row.manufacturer_part.part_identity?.part_number)
          }
        },
        {
          title: this.$t('actions'),
          key: 'action',
          align: 'center',
          width: 100,
          render: (h, params) => {
            return h('div', [
              h('Button', {
                props: {
                  type: 'text',
                  size: 'small'
                },
                on: {
                  click: () => {
                    this.removeItem(params.row.substitute_type, params.row.id)
                  }
                }
              }, this.$t('remove'))
            ])
          }
        }
      ]
    }
  },
  methods: {
    ...mapActions([
      'addItemToSubstituteCollection',
      'deleteItemFromSubstituteCollection',
      'createPartSubstituteCollection',
      'loadCertainSubstituteByPart',
      'mergeSubstituteTypeSubstituteCollections'
    ]),
    addPartToSubsituteCollection (substituteType, selectedPartId) {
      let substituteId = this.substituteCollectionIds[substituteType]
      let isNew = !substituteId

      if (!isNew) {
        this.addItem(substituteType, substituteId, selectedPartId[substituteType])
      } else {
        // create
        this.createCollection(substituteType, [selectedPartId[substituteType], this.currentPartId], this.currentPartId)
      }
    },
    createCollection (substituteType, partIds, basePartId) {
      this.saving = true
      this.createPartSubstituteCollection({
        description: this.currentPart.specification,
        substituteType: substituteType,
        specification: '',
        partIds: partIds,
        basePartId: basePartId,
        partCategoryId: null
      }).finally(() => {
        this.saving = false
      })
    },
    addItem (substituteType, substituteId, partId) {
      this.saving = true
      this.addItemToSubstituteCollection({
        substituteType: substituteType,
        substituteId: substituteId,
        partId: partId
      }).catch(error => {
        // When same part found in multiple mutual part substitute collections,
        // the system will raise this error and user may taker further step to merge the collections if needed..
        if (error.response.data.code === 1000608) {
          // Load the other collection and let user choose to merge
          this.showMergeConfirm = true
          this.loadCertainSubstituteByPart({ substituteType: substituteType, partId: partId }).then(resp => {
            this.mutualSubstituteToBeMerged = resp.data[0]
          })
        }
      }).finally(() => {
        this.saving = false
      })
    },
    removeItem (substituteType, itemId) {
      this.deleteItemFromSubstituteCollection({
        substituteType: substituteType,
        itemId: itemId,
        substituteCollectionId: this.substituteCollectionIds[substituteType]
      })
    },
    handleMerge (substituteType) {
      this.mergeSaving = false
      this.mergeSubstituteTypeSubstituteCollections({
        substituteType: substituteType,
        primarySubstituteId: this.substituteCollectionIds[substituteType],
        secondarySubstituteId: this.mutualSubstituteToBeMerged.id
      }).then(() => {
        this.$Message.info(this.$t('part.substitute_collections_merged_success'))
      }).finally(() => {
        this.mergeSaving = true
      })
      this.showMergeConfirm = false
    }
  }
}
</script>
<style lang="less" scoped>
.part-unilateral_substitute { margin-top: 32px; }
</style>
